import React, { useCallback, useEffect, useMemo, useState } from "react";
import wallpapers from "./wallpapers";

const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const Materials = ({ t }) => {
    const copy = t.materials;
    const [tab, setTab] = useState("desktop");
    const [page, setPage] = useState(0);
    const [active, setActive] = useState(null);
    const [dims, setDims] = useState(null);

    const perPage = tab === "phone" ? 7 : 6;

    const items = useMemo(() => wallpapers.filter((w) => w.device === tab), [tab]);

    const pageCount = useMemo(() => Math.max(1, Math.ceil(items.length / perPage)), [items.length, perPage]);

    const pageItems = useMemo(() => {
        const start = page * perPage;
        return items.slice(start, start + perPage);
    }, [items, page, perPage]);

    useEffect(() => {
        setPage(0);
    }, [tab]);

    const prevPage = () => setPage((p) => clamp(p - 1, 0, pageCount - 1));
    const nextPage = () => setPage((p) => clamp(p + 1, 0, pageCount - 1));
    const activeIndex = active ? items.findIndex((w) => w.id === active.id) : -1;

    const prevActive = useCallback(() => {
        if (!items.length || activeIndex === -1) return;
        const nextIndex = (activeIndex - 1 + items.length) % items.length;
        setActive(items[nextIndex]);
    }, [activeIndex, items]);

    const nextActive = useCallback(() => {
        if (!items.length || activeIndex === -1) return;
        const nextIndex = (activeIndex + 1) % items.length;
        setActive(items[nextIndex]);
    }, [activeIndex, items]);

    useEffect(() => {
        if (!active) return;

        setDims(null);

        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";

        const onKeyDown = (e) => {
            if (e.key === "Escape") setActive(null);
            if (e.key === "ArrowLeft") prevActive();
            if (e.key === "ArrowRight") nextActive();
        };

        window.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = prevOverflow;
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [active, nextActive, prevActive]);

    return (
        <section className="wallpapers" id="materials">
            <div className="wallpapers-head">
                <h1>{copy.title}</h1>

                <div className="wallpapers-tabs" role="tablist" aria-label={copy.tabsAria}>
                    <button
                        type="button"
                        className={`wallpapers-tab ${tab === "desktop" ? "is-active" : ""}`}
                        onClick={() => setTab("desktop")}
                        role="tab"
                        aria-selected={tab === "desktop"}
                    >
                        {copy.desktop}
                    </button>

                    <button
                        type="button"
                        className={`wallpapers-tab ${tab === "phone" ? "is-active" : ""}`}
                        onClick={() => setTab("phone")}
                        role="tab"
                        aria-selected={tab === "phone"}
                    >
                        {copy.phone}
                    </button>
                </div>
            </div>

            <div className={`wallpapers-grid ${tab === "phone" ? "is-phone" : "is-desktop"} is-paged`}>
                {pageItems.map((w) => (
                    <button
                        key={w.id}
                        type="button"
                        className={`wallpapers-card ${tab === "phone" ? "is-phone" : "is-desktop"}`}
                        onClick={() => setActive(w)}
                        aria-label={copy.openWallpaper}
                    >
                        <img
                            className={`wallpapers-img ${tab === "phone" ? "is-phone" : "is-desktop"}`}
                            src={w.src}
                            alt=""
                            loading="lazy"
                            draggable="false"
                        />
                    </button>
                ))}
            </div>

            <div className="wallpapers-pager" aria-label={copy.pagination}>
                <button
                    type="button"
                    className="wallpapers-page-btn glitch-btn"
                    onClick={prevPage}
                    disabled={page === 0}
                    aria-label={copy.previousPage}
                >
                    {"<"}
                </button>

                <div className="wallpapers-dots" aria-label={copy.pages}>
                    {Array.from({ length: pageCount }).map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            className={`wallpapers-dot ${i === page ? "is-active" : ""}`}
                            onClick={() => setPage(i)}
                            aria-label={`${copy.page} ${i + 1}`}
                        />
                    ))}
                </div>

                <button
                    type="button"
                    className="wallpapers-page-btn glitch-btn"
                    onClick={nextPage}
                    disabled={page === pageCount - 1}
                    aria-label={copy.nextPage}
                >
                    {">"}
                </button>
            </div>

            {active ? (
                <div className="wallpapers-modal" role="dialog" aria-modal="true" aria-label={copy.preview}>
                    <button
                        type="button"
                        className="wallpapers-backdrop"
                        aria-label={copy.closePreview}
                        onClick={() => setActive(null)}
                    />

                    <div className="wallpapers-modal-card">
                        <div className="wallpapers-modal-top">
                            <div className="wallpapers-modal-actions">
                                {dims ? (
                                    <div className="wallpapers-dims">{dims.w}x{dims.h}</div>
                                ) : (
                                    <div className="wallpapers-dims">{copy.dimensionsLoading}</div>
                                )}

                                <a className="wallpapers-download glitch-btn" href={active.src} download>
                                    {copy.download}
                                </a>

                                <button
                                    type="button"
                                    className="wallpapers-nav glitch-btn"
                                    onClick={prevActive}
                                    aria-label={copy.previousPage}
                                >
                                    {"<"}
                                </button>

                                <button
                                    type="button"
                                    className="wallpapers-close glitch-btn"
                                    onClick={() => setActive(null)}
                                    aria-label={copy.close}
                                >
                                    X
                                </button>

                                <button
                                    type="button"
                                    className="wallpapers-nav glitch-btn"
                                    onClick={nextActive}
                                    aria-label={copy.nextPage}
                                >
                                    {">"}
                                </button>
                            </div>
                        </div>

                        <div className={`wallpapers-preview-wrap ${active.device === "phone" ? "is-phone" : "is-desktop"}`}>
                            <img
                                className={`wallpapers-preview ${active.device === "phone" ? "is-phone" : "is-desktop"}`}
                                src={active.src}
                                alt=""
                                onLoad={(e) => {
                                    const img = e.currentTarget;
                                    setDims({ w: img.naturalWidth, h: img.naturalHeight });
                                }}
                                draggable="false"
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
};

export default Materials;
